import { useCallback, useRef, useEffect, useState } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFeatureFromRoute } from '../utils/memberAnalyticsMapping';

// Store active page view for tracking exit
let activePageView: {
  id: string;
  enteredAt: Date;
  pagePath: string;
} | null = null;

// Store current session
let currentSessionId: string | null = null;

/**
 * Core hook for tracking member app usage analytics
 * Handles page views, actions, and session management for club members
 * Tracks member behavior in the main golf app
 */
export function useMemberTracking() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sessionStartedRef = useRef(false);

  // Use React Query to fetch member club with proper loading states
  // This replaces the manual useEffect + useState pattern
  const { data: memberClubId, isLoading: isLoadingClub } = useQuery({
    queryKey: ['member-tracking-club', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Try to get club memberships from cache first (from useClubMemberships hook)
      const cachedMemberships = queryClient.getQueryData(['club-memberships', user.id]) as any[];

      if (cachedMemberships && cachedMemberships.length > 0) {
        // Use cached data - no duplicate query!
        const primaryClub = cachedMemberships.find(m => m.is_primary) || cachedMemberships[0];
        console.log('Member Tracking: Using cached club:', primaryClub.club_id);
        return primaryClub.club_id;
      }

      // Only fetch if not in cache (fallback)
      console.log('Member Tracking: Cache miss, fetching club membership...');
      const { data: memberships, error } = await supabase
        .from('user_club_memberships')
        .select('club_id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .limit(1)
        .single();

      if (error) {
        console.error('Member Tracking: Failed to fetch member club:', error);
        return null;
      }

      if (memberships) {
        console.log('Member Tracking: Found member club:', memberships.club_id);
        return memberships.club_id;
      }

      return null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1
  });

  // Initialize session on first use
  useEffect(() => {
    if (user && memberClubId && !sessionStartedRef.current) {
      const existingSession = localStorage.getItem('member_session_id');
      if (!existingSession || !isValidSession(existingSession)) {
        startSession();
      } else {
        // Parse the JSON to get just the session ID
        try {
          const sessionData = JSON.parse(existingSession);
          currentSessionId = sessionData.id;
        } catch (error) {
          console.error('Member Tracking: Invalid session data, starting new session');
          startSession();
        }
      }
      sessionStartedRef.current = true;
    }
  }, [user, memberClubId]);

  // Cleanup on unmount
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activePageView) {
        updatePageViewExit(activePageView.id, activePageView.enteredAt);
      }
      if (currentSessionId) {
        endSession(currentSessionId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /**
   * Track page view - called when user navigates to a new page
   */
  const trackPageView = useCallback(async (pagePath: string) => {
    // Don't attempt tracking if we're still loading club data
    if (isLoadingClub) {
      console.log('Member Tracking: Waiting for club data to load...');
      return;
    }

    // Only warn if loading is complete but no club found
    if (!user || !memberClubId) {
      if (!isLoadingClub) {
        console.warn('Member Tracking: User or club not available for page view tracking');
        console.warn('Member Tracking: user:', !!user, 'club:', !!memberClubId);
      }
      return;
    }

    console.log('Member Tracking: Starting page view tracking for', pagePath);
    console.log('Member Tracking: User ID:', user.id, 'Club ID:', memberClubId);

    try {
      // End previous page view if exists
      if (activePageView) {
        await updatePageViewExit(activePageView.id, activePageView.enteredAt);
      }

      // Get feature mapping
      const featureMapping = getFeatureFromRoute(pagePath);
      if (!featureMapping) {
        console.warn(`Member Tracking: No feature mapping found for route: ${pagePath}`);
        return;
      }

      // Ensure we have a session
      if (!currentSessionId) {
        currentSessionId = await startSession();
      }

      // Detect device and browser
      const deviceType = getDeviceType();
      const browser = getBrowser();
      const screenResolution = `${window.screen.width}x${window.screen.height}`;

      // Insert page view record
      const { data, error } = await supabase
        .from('crm_page_views')
        .insert({
          club_id: memberClubId,
          user_id: user.id,
          user_type: 'member',  // 🆕 Member tracking
          page_path: pagePath,
          feature_name: featureMapping.featureName,
          entered_at: new Date().toISOString(),
          device_type: deviceType,
          browser: browser,
          screen_resolution: screenResolution,
          referrer_page: activePageView?.pagePath || null,
          session_id: currentSessionId
        })
        .select()
        .single();

      if (error) {
        console.error('Member Tracking: Failed to track page view:', error);
        console.error('Member Tracking: Error details:', JSON.stringify(error, null, 2));
        return;
      }

      // Store for exit tracking
      activePageView = {
        id: data.id,
        enteredAt: new Date(),
        pagePath: pagePath
      };

      console.log(`Member Tracking: Page view tracked - ${featureMapping.featureName}`);

    } catch (error) {
      console.error('Member Tracking: Error in trackPageView:', error);
    }
  }, [user, memberClubId, isLoadingClub]);

  /**
   * Track member action - called when user performs an action
   */
  const trackAction = useCallback(async (
    actionType: string,
    success: boolean = true,
    metadata: any = {},
    errorMessage?: string
  ) => {
    // Don't attempt tracking if we're still loading club data
    if (isLoadingClub) {
      console.log('Member Tracking: Waiting for club data to load...');
      return;
    }

    if (!user || !memberClubId || !currentSessionId) {
      if (!isLoadingClub) {
        console.warn('Member Tracking: Missing context for action tracking');
      }
      return;
    }

    try {
      // Get current feature from active page view
      const featureMapping = activePageView ?
        getFeatureFromRoute(activePageView.pagePath) : null;

      if (!featureMapping) {
        console.warn('Member Tracking: No feature context for action tracking');
        return;
      }

      // Insert action record
      const { error } = await supabase
        .from('crm_actions')
        .insert({
          club_id: memberClubId,
          user_id: user.id,
          user_type: 'member',  // 🆕 Member tracking
          action_type: actionType,
          feature_name: featureMapping.featureName,
          page_path: activePageView?.pagePath,
          success: success,
          error_message: errorMessage || null,
          metadata: metadata,
          session_id: currentSessionId
        });

      if (error) {
        console.error('Member Tracking: Failed to track action:', error);
        return;
      }

      console.log(`Member Tracking: Action tracked - ${actionType} (${success ? 'success' : 'failed'})`);

    } catch (error) {
      console.error('Member Tracking: Error in trackAction:', error);
    }
  }, [user, memberClubId, isLoadingClub]);

  /**
   * Start a new session
   */
  const startSession = useCallback(async (): Promise<string> => {
    if (!user || !memberClubId) {
      throw new Error('Member Tracking: Cannot start session without user and club');
    }

    try {
      const sessionId = crypto.randomUUID();
      const deviceType = getDeviceType();
      const browser = getBrowser();
      const ipAddress = await getClientIP();

      // Insert session record
      const { error } = await supabase
        .from('crm_sessions')
        .insert({
          id: sessionId,
          club_id: memberClubId,
          user_id: user.id,
          user_type: 'member',  // 🆕 Member tracking
          started_at: new Date().toISOString(),
          device_type: deviceType,
          browser: browser,
          ip_address: ipAddress,
          is_mobile: deviceType === 'mobile'
        });

      if (error) {
        console.error('Member Tracking: Failed to start session:', error);
        console.error('Member Tracking: Session error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      // Store in localStorage with expiration
      const sessionData = {
        id: sessionId,
        startedAt: Date.now(),
        clubId: memberClubId
      };
      localStorage.setItem('member_session_id', JSON.stringify(sessionData));

      currentSessionId = sessionId;
      console.log(`Member Tracking: Session started for member:`, sessionId);

      return sessionId;

    } catch (error) {
      console.error('Member Tracking: Error starting session:', error);
      throw error;
    }
  }, [user, memberClubId]);

  /**
   * End the current session
   */
  const endSession = useCallback(async (sessionId: string) => {
    if (!sessionId) return;

    try {
      // Get session stats
      const { data: pageViews } = await supabase
        .from('crm_page_views')
        .select('id, feature_name')
        .eq('session_id', sessionId);

      const { data: actions } = await supabase
        .from('crm_actions')
        .select('id')
        .eq('session_id', sessionId);

      const uniqueFeatures = new Set(pageViews?.map(pv => pv.feature_name) || []);

      // Update session record
      const { error } = await supabase
        .from('crm_sessions')
        .update({
          ended_at: new Date().toISOString(),
          duration_minutes: calculateSessionDuration(sessionId),
          pages_visited: pageViews?.length || 0,
          actions_taken: actions?.length || 0,
          unique_features_used: uniqueFeatures.size
        })
        .eq('id', sessionId);

      if (error) {
        console.error('Member Tracking: Failed to end session:', error);
      } else {
        console.log('Member Tracking: Member session ended:', sessionId);
      }

      // Clear localStorage
      localStorage.removeItem('member_session_id');
      currentSessionId = null;

    } catch (error) {
      console.error('Member Tracking: Error ending session:', error);
    }
  }, []);

  return {
    trackPageView,
    trackAction,
    startSession,
    endSession,
    getCurrentSessionId: () => currentSessionId
  };
}

/**
 * Update page view with exit timestamp and duration
 */
async function updatePageViewExit(pageViewId: string, enteredAt: Date) {
  try {
    const exitedAt = new Date();
    const durationSeconds = Math.floor((exitedAt.getTime() - enteredAt.getTime()) / 1000);
    const isBounce = durationSeconds < 10;

    await supabase
      .from('crm_page_views')
      .update({
        exited_at: exitedAt.toISOString(),
        duration_seconds: durationSeconds,
        is_bounce: isBounce
      })
      .eq('id', pageViewId);

    console.log(`Member Tracking: Page view updated - ${durationSeconds}s (bounce: ${isBounce})`);

  } catch (error) {
    console.error('Member Tracking: Error updating page view exit:', error);
  }
}

/**
 * Check if session is still valid (not expired)
 */
function isValidSession(sessionJson: string): boolean {
  try {
    const session = JSON.parse(sessionJson);
    const sessionAge = Date.now() - session.startedAt;
    const maxAge = 30 * 60 * 1000; // 30 minutes

    return sessionAge < maxAge;
  } catch {
    return false;
  }
}

/**
 * Calculate session duration in minutes
 */
function calculateSessionDuration(sessionId: string): number {
  try {
    const sessionData = localStorage.getItem('member_session_id');
    if (!sessionData) return 0;

    const session = JSON.parse(sessionData);
    const durationMs = Date.now() - session.startedAt;
    return Math.floor(durationMs / (1000 * 60));
  } catch {
    return 0;
  }
}

/**
 * Detect device type
 */
function getDeviceType(): string {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Detect browser
 */
function getBrowser(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('chrome')) return 'chrome';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('safari')) return 'safari';
  if (userAgent.includes('edge')) return 'edge';
  return 'other';
}

/**
 * Get client IP address (simplified)
 */
async function getClientIP(): Promise<string | null> {
  try {
    // This is a simple approach - in production you might want to use a proper IP detection service
    return '0.0.0.0'; // Placeholder - could integrate with ipify.org or similar
  } catch {
    return null;
  }
}
